// Import this file whereever you want to use aha web components within react. It automatically handles converting
// camelCased attributes to kebab-cased attributes, handles complex attributes, as well as builds custom DOM events
// out of react's synthetic event system.
import React, { useRef, useLayoutEffect } from 'react';

type WebComponentTagName = `aha-${string}` | `fa-${string}`;

type EventRef = Parameters<HTMLElement['addEventListener']>;

// Get an array of all possible native events for an HTML element.
const nativeEvents: string[] = [];
let property;
for (property in HTMLElement.prototype) {
  if (/^on/.test(property)) {
    nativeEvents.push(property);
  }
}

function reactifyWebComponent(tagName: WebComponentTagName) {
  return props => {
    // NOTE: If other pre-built custom element libraries are used we can use a similar check to pass props to them.
    return useAhaElement(tagName, props);
  };
}

class ReactifyWebComponentError extends Error {}

function useAhaElement(
  tagName: WebComponentTagName,
  props: React.PropsWithChildren<{ [p: string]: unknown }> = {}
) {
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;

    const removeEvents: EventRef[] = [];

    Object.entries(props).forEach(([key, value]) => {
      // `children` and `style` are special props, and should be passed through unaltered.
      if (['children', 'style'].includes(key)) return;

      if (typeof value === 'function') {
        // By default React uses the `on` prefix for event listeners. Web components use custom events to listen for events, and by convention we use the tag name as a prefix for the event name. This allows us to easily listen for custom event names on the reactified web components. Ex. `<my-component onFoo={...}>` adds the custom event listener `host.addEventListener('my-component:foo', ...)` to the web component instance.
        //
        // There is a limitation though: if the custom event name would be reduced to a native event name, then the event name will reference the native event rather than the custom event. Ex. `onChange` would be reduced to `change` and so would use the native `change` event and not the `my-component:change` event. If we specifically wanted to listen to `my-component:change` event, the basic logic above would not satisfy that requirement.
        //
        // Since it is not always practical to refactor existing web components to use a different event name, and since Stencil hides away the native event names in such a way that they are innaccessible at runtime, we need a way to force the event name to be converted to a custom event name.
        //
        // To work around this limitation, we can use the `onHost` prefix to force the event name to be converted to a custom event name. Ex. `<my-component onHostChange={...}>` would add the custom event listener `host.addEventListener('my-component:change', ...)` to the web component instance and _would not_ add the native `change` event listener. The `onHost` prefix is chosen because web components are referred to as the "host" element from within the shadowDOM.
        //
        // ----- Examples: ----- //
        //
        // `onFoo={...}` == `host.addEventListener('tag-name:foo', ...)` // `foo` is not a native event
        //
        // `onHostChange={...}` == `host.addEventListener('tag-name:change', ...)` // `change` is a native event, but usage of the custom event name is forced
        //
        // `onChange={...}` == `host.addEventListener('change', ...)` // `change` is a native event
        //
        if (/^on.+/.test(key)) {
          // Pass events to web components.
          const fullEventName = key.replace(/Capture$/, '').toLowerCase();
          let event = key
            .replace(/[A-Z]/g, l => `-${l.toLowerCase()}`) // Convert camelCase to kebab-case
            .replace(/^on-?(host-?)?|-capture$/g, '')
            .toLowerCase();
          const capture = /Capture$/.test(key);

          // If event is a custom event (ex. onUnmounted) then create a native custom event for the web component to call.
          // Ex. `onUnmounted` would fire the event `tag-name:unmounted` where `tag-name` is the tag name of the custom element.
          if (!nativeEvents.includes(fullEventName)) {
            event = `${tagName}:${event}`;
          }
          const eventHandler = function (args) {
            value(args);
          };
          const args: EventRef = [event, eventHandler, { capture }];
          removeEvents.push(args);
          host.addEventListener(...args);
        } else {
          // Bind the element and props to the function instance.
          host[key] = value.bind(null, host, props);
        }
      } else {
        host[key] = value;
      }
    });

    // Remove classname attribute from element, as it's just an artifact.
    host.removeAttribute('classname');

    return () => {
      for (const args of removeEvents) {
        host.removeEventListener(...args);
      }
    };
  }, [props, ref, tagName]);

  // Prepare props to be rendered in the DOM
  const renderedProps = Object.entries(props).reduce((acc, [key, value]) => {
    // Children are rendered manually through createElement
    if (key === 'children') {
      return acc;
    }

    switch (typeof value) {
      case 'string':
      case 'number':
        // Direct pass-thru
        break;

      case 'boolean':
        // boolean values that have any value are treated truthy, even values like 'false', so
        // pass null if that's the case
        value = value ? '' : null;
        break;

      case 'function':
        // Do not render functions
        return acc;

      case 'object':
        if (key !== 'style') {
          try {
            value = JSON.stringify(value);
          } catch (e) {
            throw new ReactifyWebComponentError(
              `Failed to convert object to JSON string in element ${tagName} prop key ${key}: ${e.message}`
            );
          }
        }
        break;
    }

    acc[key] = value;
    return acc;
  }, {});

  return React.createElement(
    tagName,
    { ...renderedProps, ref },
    props.children
  );
}

export const AhaAvatar = reactifyWebComponent('aha-avatar');
export const AhaButton = reactifyWebComponent('aha-button');
export const AhaCopyButton = reactifyWebComponent('aha-copy-button');
export const AhaColorPicker = reactifyWebComponent('aha-color-picker');
export const AhaFlex = reactifyWebComponent('aha-flex');
export const AhaIcon = reactifyWebComponent('aha-icon');
export const AhaMenu = reactifyWebComponent('aha-menu');
export const AhaMenuContent = reactifyWebComponent('aha-menu-content');
export const AhaMenuGroup = reactifyWebComponent('aha-menu-group');
export const AhaMenuItem = reactifyWebComponent('aha-menu-item');
export const AhaModal = reactifyWebComponent('aha-modal');
export const AhaProgressBar = reactifyWebComponent('aha-progress-bar');
export const AhaSlider = reactifyWebComponent('aha-slider');
export const AhaModalBody = reactifyWebComponent('aha-modal-body');
export const AhaModalFooter = reactifyWebComponent('aha-modal-footer');
export const AhaModalHeader = reactifyWebComponent('aha-modal-header');
export const AhaPageThumbnail = reactifyWebComponent('aha-page-thumbnail');
export const AhaRadialProgress = reactifyWebComponent('aha-radial-progress');
export const AhaRadialProgressLine = reactifyWebComponent(
  'aha-radial-progress-line'
);
export const AhaRadioButtonGroup = reactifyWebComponent(
  'aha-radio-button-group'
);
export const AhaRadioButton = reactifyWebComponent('aha-radio-button');
export const AhaSearchField = reactifyWebComponent('aha-search-field');
export const AhaSidepanel = reactifyWebComponent('aha-sidepanel');
export const AhaSpinner = reactifyWebComponent('aha-spinner');
export const AhaUploader = reactifyWebComponent('aha-uploader');
export const AhaTooltip = reactifyWebComponent('aha-tooltip');
export const FaIcon = reactifyWebComponent('fa-icon');
export const AhaBadge = reactifyWebComponent('aha-badge');
export const AhaCalendar = reactifyWebComponent('aha-calendar');
export const AhaDatePicker = reactifyWebComponent('aha-datepicker');
export const AhaDatePickerRange = reactifyWebComponent('aha-datepicker-range');
export const AhaTimeRangeInput = reactifyWebComponent('aha-time-range-input');
export const AhaAlert = reactifyWebComponent('aha-alert');
